import React, { useEffect, useState } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { ChatGPTUnfilled, CheckmarkIcon, GoldCalendar } from '@/assets/images';
import type { RootState } from '@/rootReducer';
import type { Business } from '@/modules/dashboard/dashboardTypes';
import type { EmailTemplate } from '@/modules/email/emailTypes';
import Loader, { Status } from '@/components/Loader/Loader';

type Props = {
	business: Business;
	canHide: boolean;
	isActive: boolean;
	onSelect: (business: Business) => void;
	onHide: (id: string) => void;
	index: number;
	businesses: Business[];
	emailModalScrollRef: React.Ref<HTMLDivElement>;
	selectedTemplate: EmailTemplate;
};

const EmailListItem = ({
	business,
	canHide,
	onSelect,
	onHide,
	isActive,
	index,
	businesses,
	emailModalScrollRef,
	selectedTemplate
}: Props): JSX.Element => {
	const [
		emailSent,
		emailQueue,
		scheduledEmailActivity,
		aiText,
		aiLoading,
		selectedList
	] = useSelector(
		(state: RootState) => [
			state.email.getIn(['email', 'sent']).toJS(),
			state.email.getIn(['email', 'queue']).toJS(),
			state.email.getIn(['email', 'scheduled', 'activity']).toJS(),
			state.email.getIn(['ai', 'text']).toJS(),
			state.email.getIn(['ai', 'loading']),
			state.dashboard.getIn(['lists', 'selected']).toJS()
		],
		shallowEqual
	);
	const [didMount, setDidMount] = useState<boolean>(false);

	useEffect(() => {
		setDidMount(true);
	}, []);

	useEffect(() => {
		if (emailQueue[business._id] && index < businesses.length - 1) {
			onSelect(businesses[index + 1]);

			const overallPosition = index * 64;
			const windowScrollPosition =
				emailModalScrollRef?.current?.scrollTop +
				emailModalScrollRef?.current?.clientHeight -
				128;

			if (overallPosition > windowScrollPosition) {
				emailModalScrollRef?.current?.scroll(
					0,
					emailModalScrollRef?.current?.scrollTop + 64
				);
			}
		}
	}, [emailQueue[business._id]]);

	useEffect(() => {
		if (
			!isEmpty(scheduledEmailActivity[business._id]) &&
			index < businesses.length - 1 &&
			didMount
		) {
			onSelect(businesses[index + 1]);

			const overallPosition = index * 64;
			const windowScrollPosition =
				emailModalScrollRef?.current?.scrollTop +
				emailModalScrollRef?.current?.clientHeight -
				128;

			if (overallPosition > windowScrollPosition) {
				emailModalScrollRef?.current?.scroll(
					0,
					emailModalScrollRef?.current?.scrollTop + 64
				);
			}
		}
	}, [isEmpty(scheduledEmailActivity[business._id])]);

	return (
		<div
			className={`emailList-item flex items-center justify-between transition duration-300 ease-in-out ${isActive ? 'emailList-item--active' : ''} ${aiText?.[`${selectedList.id}`]?.[`${selectedTemplate?.id}`]?.[`${business._id}`] && selectedTemplate.body?.includes('[ai_snippet]') ? 'ai' : ''} ${aiLoading && !aiText?.[`${selectedList.id}`]?.[`${selectedTemplate?.id}`]?.[`${business._id}`] && selectedTemplate.body?.includes('[ai_snippet]') ? 'aiLoading' : ''} `}
		>
			<button
				className="emailList-button w-10/12 p-5 text-left text-white"
				onClick={() => {
					onSelect(business);
				}}
				type="button"
			>
				{business.name}
			</button>
			{!emailSent[business._id] &&
				!emailQueue[business._id] &&
				!scheduledEmailActivity[business._id] && (
					<button
						onClick={() => {
							onHide(business._id);
							onSelect(businesses[index + 1]);
						}}
						type="button"
						className="emailList-hide mx-2 whitespace-nowrap text-sm text-orange"
					>
						Hide
					</button>
				)}
			{emailQueue[business._id] &&
				!scheduledEmailActivity[business._id] &&
				!aiText?.[`${selectedList.id}`]?.[`${selectedTemplate?.id}`]?.[
					`${business._id}`
				] &&
				!selectedTemplate.body?.includes('[ai_snippet]') && (
					<div className="emailList-sent mx-5 whitespace-nowrap text-sm">
						Sending
					</div>
				)}
			{scheduledEmailActivity[business._id] &&
				!aiText?.[`${selectedList.id}`]?.[`${selectedTemplate?.id}`]?.[
					`${business._id}`
				] && (
					<>
						<GoldCalendar
							className="emailList-item-scheduled-icon"
							id="emailList-scheduled-icon"
						/>
						<span className="emailList-item-scheduled-hovertext">
							An email is scheduled for this business
						</span>
					</>
				)}
			{aiText?.[`${selectedList.id}`]?.[`${selectedTemplate?.id}`]?.[
				`${business._id}`
			] &&
				selectedTemplate.body?.includes('[ai_snippet]') &&
				!scheduledEmailActivity[business._id] &&
				!emailQueue[business._id] && (
					<ChatGPTUnfilled className="emailList-item-chat-gpt-icon" />
				)}
			{scheduledEmailActivity[business._id] &&
				aiText?.[`${selectedList.id}`]?.[`${selectedTemplate?.id}`]?.[
					`${business._id}`
				] &&
				selectedTemplate.body?.includes('[ai_snippet]') && (
					<div className="emailList-item-icon-group">
						<ChatGPTUnfilled className="emailList-item-icon-group-chat-gpt" />
						<GoldCalendar className="emailList-item-icon-group-scheduled" />
						<span className="emailList-item-icon-group-scheduled-hovertext">
							An email is scheduled for this business
						</span>
					</div>
				)}
			{emailQueue[business._id] &&
				!scheduledEmailActivity[business._id] &&
				aiText?.[`${selectedList.id}`]?.[`${selectedTemplate?.id}`]?.[
					`${business._id}`
				] &&
				selectedTemplate.body?.includes('[ai_snippet]') && (
					<div className="emailList-item-icon-group">
						<ChatGPTUnfilled className="emailList-item-icon-group-chat-gpt" />
						<div className="emailList-item-icon-group-sent">
							Sending
						</div>
					</div>
				)}
			{aiLoading &&
				!aiText?.[`${selectedList.id}`]?.[`${selectedTemplate?.id}`]?.[
					`${business._id}`
				] &&
				selectedTemplate.body?.includes('[ai_snippet]') && (
					<Loader
						className="emailList-item-ai-loading"
						status={Status.LOADING}
					/>
				)}
		</div>
	);
};

export default EmailListItem;
